<template>
  <div>
    <h2 class="text-center text-[28px] font-semibold">短信登录</h2>
    <p class="mt-[4px] text-center text-base">请使用手机验证码登录</p>
    <Form class="my-6 login-message-form" @submit.prevent="onClickLoginOrRegister">
      <FormItem>
        <Input v-model="phone" placeholder="请输入手机号" @on-change="error = ''" @on-keydown="onInputPhoneNumber">
          <template #prepend>
            <Select v-model="country" @on-change="error = ''">
              <Option v-for="country in Countries" :key="country.code" :value="country.code" :label="`+${country.code}`">
                <span>{{ country.name_cn }} +{{ country.code }}</span>
              </Option>
            </Select>
          </template>
        </Input>
      </FormItem>
      <FormItem>
        <div id="login-captcha-container">
          <Captcha ref="captchaInstance" :key="captchaRenderKey" container="#login-captcha-container" @success="onCaptchaSuccess"></Captcha>
        </div>
      </FormItem>
      <FormItem v-if="sent">
        <Input v-model="code" placeholder="请输入 6 位短信验证码">
          <template #append>
            <div v-if="seconds > 0" class="px-2 text-disabled select-none">{{ seconds }} 秒后重新发送</div>
            <div v-else class="bg-primary-main text-white leading-[48px] cursor-pointer select-none" @click="onClickSendMessage">发送验证码</div>
          </template>
        </Input>
      </FormItem>
      <FormItem v-if="error" class="min-h-0">
        <Alert type="error" show-icon class="m-0">{{ error }}</Alert>
      </FormItem>
      <FormItem>
        <Button @click="onClickLoginOrRegister" html-type="submit" type="primary" size="large" long class="h-12 rounded-md">登录&nbsp;/&nbsp;注册</Button>
      </FormItem>
    </Form>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted } from 'vue';
import axios from 'axios';
import getErrorMessage from '@/utils/get-error-message';
import getAWSCTokens from '@/utils/get-awsc-tokens';
import Countries from '@/constants/country-list';
import Captcha from '@/components/shared/captcha.vue';
import useLoginCheck from '@/composables/login-check.ts';
import watchTargetResize from '@/utils/watch-target-resize.ts';
import trackEvent from '@/utils/track-event';

const country = ref('86');
const phone = ref('');
const code = ref('');
const sent = ref(false);
const error = ref('');
const captchaRenderKey = ref(0);
const captchaInstance = ref(null);
const captchaData = ref(null);
const accountStatus = ref('');

const seconds = ref(60);
const timer = ref<number | NodeJS.Timeout>(0);

const createSession = async () => {
  try {
    await axios.post('/api/auth/session');
  } catch (e) {
    error.value = '创建 Session 失败，请稍后重试';
  }
};

const onCaptchaSuccess = (data: any) => {
  captchaData.value = data;
  onClickSendMessage();
};

const onClickSendMessage = async () => {
  if (!captchaData.value) {
    error.value = '请先完成验证';
    return;
  }
  if (!phone.value.match(/^\d{7,}$/)) {
    (captchaInstance.value as any).init();
    error.value = '请输入有效的手机号';
    return;
  }
  try {
    const payload = {
      phone: phone.value,
      countryCode: country.value,
      sig: (captchaData.value as any).sig,
      token: (captchaData.value as any).token,
      sessionId: (captchaData.value as any).sessionId
    };
    const { data } = await axios.post('/api/auth/sendRegisterSmsCode', payload);
    accountStatus.value = data?.status || '';
    sent.value = true;
    seconds.value = 60;
    timer.value = setInterval(() => {
      if (seconds.value <= 0) {
        (captchaInstance.value as any).init();
        clearInterval(timer.value);
        sent.value = false;
        captchaData.value = null;
        error.value = '';
      }
      seconds.value = seconds.value - 1;
    }, 1000);
  } catch (e: any) {
    (captchaInstance.value as any).init();
    sent.value = false;
    seconds.value = 0;
    error.value = getErrorMessage(e);
  }
};

const onClickLoginOrRegister = async () => {
  if (!phone.value) {
    error.value = '请输入手机号';
    return;
  }
  if (!sent.value) {
    error.value = '请先获取验证码';
    return;
  }
  if (!code.value) {
    error.value = '请输入验证码';
    return;
  }
  const { webUmidToken, uaToken } = getAWSCTokens();
  const payload: { [key: string]: string } = {
    webUmidToken,
    uaToken,
    code: code.value
  };
  try {
    if (accountStatus.value === 'ACTIVE') {
      // 登录
      const { data } = await axios.post('/api/auth/loginBySmsCode', payload);
      if (data?.code === 4) {
        error.value = data?.message || '';
        return;
      }
      trackEvent('click_register', 'phone', 'community');
      useLoginCheck();
    }
    if (accountStatus.value === 'NOT_FOUND') {
      // 注册
      trackEvent('click_register', 'phone', 'community');
      const redirect = new URLSearchParams(window.location.search)?.get('redirect') || '';
      if (redirect) payload.registerFrom = redirect;
      await axios.post('/api/users/registerByPhone', payload);
    }
    if (accountStatus.value === 'BANNED') {
      error.value = '此账号已被封禁';
    }
  } catch (e: any) {
    error.value = getErrorMessage(e);
  }
};

const onInputPhoneNumber = (event: KeyboardEvent) => {
  // TODO replace keyCode with key or code
  // 数字键 - 48-57 & 96-105
  // 退格键 - 8
  // 删除键 - 46
  // 左箭头 - 37
  // 右箭头 - 39
  const code = event.keyCode;
  if (!(code >= 48 && code <= 57) && !(code >= 96 && code <= 105)) {
    [8, 46, 37, 39].includes(code) || event.preventDefault();
  }
};

onMounted(() => {
  createSession();
  // TODO 更为优雅的实现
  setTimeout(() => {
    const target = document.querySelector('#login-captcha-container') as HTMLElement;
    watchTargetResize(target, () => (captchaRenderKey.value = Date.now()));
  }, 500);
});
</script>

<style lang="scss">
.login-message-form {
  .ivu-form-item {
    min-height: 48px;
    margin-bottom: 16px;
  }
  .ivu-input-wrapper {
    input {
      height: 48px !important;
    }
  }
  .ivu-input-group-with-prepend {
    .ivu-input-group-prepend {
      width: 80px;
      border-top-left-radius: 6px;
      border-bottom-left-radius: 6px;
    }
    input {
      border-top-right-radius: 6px;
      border-bottom-right-radius: 6px;
    }
  }
  .ivu-input-group-with-append {
    .ivu-input-group-append {
      min-width: 90px;
      border-top-right-radius: 6px;
      border-bottom-right-radius: 6px;
      overflow: hidden;
      padding: 0px;
      height: 48px;
    }
    input {
      border-top-left-radius: 6px;
      border-bottom-left-radius: 6px;
    }
  }
}
</style>
